import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Row, Col, Radio, Select, Typography, Flex, Layout, Spin } from 'antd';
import { useRecoilValue } from 'recoil';
import { appConfigAtomState } from '../../../state/reducerAtoms/app.atom';

import AppTable from '../../../components/table/index';

import { getCurrentFacilityURL } from '../../../helpers/helper.functions';
import { getAuthToken } from '../../../helpers/auth.helper';
import config from '../../../config/config';
import { translateThis } from '../../../helpers/language.helper';
import PageLayout from '../../app';

import { DATE_FORMAT, DATE_FORMAT_YEAR_TIME_SHORT } from '../../../constants';
import ApiCaller from '../../../services/api/general';
import SVGIcons from '../../../assets/images/icons/svgIcons';
import './index.scss';
import AppIcons from '../../../assets/images/icons';
import { AppBarChart } from '../../../components/charts/bar.chart';
import { getDashboardData } from '../../../services/api/dashboard';

const { Title, Paragraph } = Typography;
const { Header, Content, Sider } = Layout;

const STATUS_COLORS = {
    'Resolved': '#4CE98F',
    'Not resolved': '#9F9FF8',
    'Human Req.': '#162550',
    'Next Step': '#7459D9',
    'Unknown': '#AAAAAA',
};

const FREQUENCY_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'All time', value: 'alltime' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatPeriodLabel = (period) => {
    if (!period) return period;
    // Monthly: "2026-03" → "Mar '26"
    const monthMatch = String(period).match(/^(\d{4})-(\d{2})$/);
    if (monthMatch) {
        const year = monthMatch[1].slice(2);
        const month = parseInt(monthMatch[2], 10) - 1;
        return `${MONTH_NAMES[month]} '${year}`;
    }
    // Weekly: "2026-W10" or "2026-W1" → "Wk. 10 2026"
    const weekMatch = String(period).match(/^(\d{4})-W(\d{1,2})$/i);
    if (weekMatch) {
        return `Wk. ${parseInt(weekMatch[2], 10)} ${weekMatch[1]}`;
    }
    return period;
};

const formatNum = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return String(value);
};

const xAxisOptions = {
    stacked: true,
    ticks: { color: '#FFFFFF' },
    grid: { display: false, borderDash: [5, 5], color: '#ffffff', drawOnChartArea: true },
};

const yAxisBase = {
    stacked: true,
    ticks: {
        color: '#FFFFFF',
        display: true,
        callback: (value) => formatNum(value),
    },
    grid: { display: false, color: '#ffffff', borderDash: [5, 5], drawOnChartArea: true },
};

const SHOW_STATIC_CHARTS = false;

const DashboardPage = () => {
    const appState = useRecoilValue(appConfigAtomState);
    const [frequency, setFrequency] = useState(() => localStorage.getItem('dashboard_frequency') || 'alltime');
    const [moduleId, setModuleId] = useState(() => localStorage.getItem('dashboard_moduleId') || null);
    const [modules, setModules] = useState([]);
    const [dashData, setDashData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeSessions, setActiveSessions] = useState(null);
    const activeSessionsIntervalRef = useRef(null);

    useEffect(() => {
        const fetchModules = async () => {
            const apiCaller = new ApiCaller('modules');
            const result = await apiCaller.getList();
            // getList returns the data object directly: { results: [], totalCount: N }
            const list = result?.results || result?.data?.results || [];
            setModules(list.map(m => ({ ...m, id: m._id || m.id })));
        };
        fetchModules();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const result = await getDashboardData(frequency, moduleId);
            if (result.success) {
                setDashData(result.data);
                // Seed the live counter from the initial full fetch
                if (result.data?.activeSessions !== undefined) {
                    setActiveSessions(result.data.activeSessions);
                }
            }
            setIsLoading(false);
        };
        fetchData();
    }, [frequency, moduleId]);

    // SSE — single persistent connection, no repeated HTTP requests
    useEffect(() => {
        const token = getAuthToken();
        const connectionrequestor = getCurrentFacilityURL();

        if (!token || !connectionrequestor) return undefined;

        const params = new URLSearchParams({ token, connectionrequestor });
        if (moduleId) params.set('moduleId', moduleId);

        const url = `${config.apiURL}dashboard/active-sessions/stream?${params.toString()}`;
        const es = new EventSource(url);

        es.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                if (data.activeSessions !== undefined) setActiveSessions(data.activeSessions);
            } catch { /* ignore malformed frames */ }
        };

        activeSessionsIntervalRef.current = es;
        return () => es.close();
    }, [moduleId]);

    const isFlat = frequency === 'today' || frequency === 'alltime';

    // API Chart 1: ticketsByStatus
    const getApiChart1Data = () => {
        if (!dashData) return null;
        if (isFlat) {
            const statuses = dashData.ticketsByStatus || [];
            if (!statuses.length) return null;
            return {
                labels: statuses.map(s => s.status),
                datasets: [{
                    label: 'Tickets',
                    data: statuses.map(s => s.count),
                    backgroundColor: statuses.map(s => STATUS_COLORS[s.status] || '#9F9FF8'),
                    barThickness: 35,
                    borderRadius: 10,
                }],
            };
        }
        const periods = dashData.ticketsByStatus || [];
        if (!periods.length) return null;
        const allStatuses = ['Human Req.', 'Not resolved', 'Resolved', 'Next Step'];
        return {
            labels: periods.map(p => formatPeriodLabel(p.period)),
            datasets: allStatuses.map(status => ({
                label: status,
                data: periods.map(p => p.statuses?.find(s => s.status === status)?.count || 0),
                backgroundColor: STATUS_COLORS[status] || '#9F9FF8',
                barThickness: 20,
                borderRadius: 10,
            })),
        };
    };

    const INTENT_COLORS = ['#9F9FF8', '#7459D9', '#4CE98F', '#162550', '#26AD5F', '#FF8C42', '#E84855'];

    // API Chart 2: intentTriggers
    const getApiChart2Data = () => {
        if (!dashData) return null;
        const triggers = dashData.intentTriggers || [];
        if (!triggers.length) return null;

        if (isFlat) {
            // flat: array of { intentName, count }
            return {
                labels: triggers.map(i => i.intentName),
                datasets: [{
                    label: 'Triggers',
                    data: triggers.map(i => i.count),
                    backgroundColor: '#9F9FF8',
                    barThickness: 35,
                    borderRadius: 10,
                }],
            };
        }

        // grouped: array of { period, total, intents[] }
        // x-axis = periods, one dataset per unique intent name
        const periods = triggers.map(p => formatPeriodLabel(p.period));
        const intentNames = [...new Set(triggers.flatMap(p => (p.intents || []).map(i => i.intentName)))];
        if (!intentNames.length) return null;

        return {
            labels: periods,
            datasets: intentNames.map((name, idx) => ({
                label: name,
                data: triggers.map(p => (p.intents || []).find(i => i.intentName === name)?.count || 0),
                backgroundColor: INTENT_COLORS[idx % INTENT_COLORS.length],
                barThickness: 20,
                borderRadius: 10,
            })),
        };
    };

    // API Chart 3: ticketCounts over time
    const getApiChart3Data = () => {
        if (!dashData) return null;
        if (isFlat) {
            const total = typeof dashData.ticketCounts === 'number' ? dashData.ticketCounts : 0;
            if (!total) return null;
            return {
                labels: [frequency === 'today' ? 'Today' : 'Total'],
                datasets: [{
                    label: 'Tickets',
                    data: [total],
                    backgroundColor: '#9F9FF8',
                    barThickness: 60,
                    borderRadius: 10,
                }],
            };
        }
        const periods = dashData.ticketCounts || [];
        if (!periods.length) return null;
        return {
            labels: periods.map(p => formatPeriodLabel(p.period)),
            datasets: [{
                label: 'Tickets',
                data: periods.map(p => p.count),
                backgroundColor: '#9F9FF8',
                barThickness: 25,
                borderRadius: 10,
            }],
        };
    };


    const apiChart1Data = getApiChart1Data();
    const apiChart2Data = getApiChart2Data();
    const apiChart3Data = getApiChart3Data();

    // ── Section 2: Bar charts ──
    const s2MonitorData = (() => {
        if (!dashData) return null;
        if (isFlat) {
            const statuses = dashData.ticketsByStatus || [];
            if (!statuses.length) return null;
            return {
                labels: statuses.map(s => s.status),
                datasets: [{ label: 'Tickets', data: statuses.map(s => s.count), backgroundColor: statuses.map(s => STATUS_COLORS[s.status] || '#9F9FF8'), barThickness: 35, borderRadius: 10 }],
            };
        }
        const periods = dashData.ticketsByStatus || [];
        if (!periods.length) return null;
        return {
            labels: periods.map(p => formatPeriodLabel(p.period)),
            datasets: ['Human Req.', 'Not resolved', 'Resolved', 'Next Step'].map(status => ({
                label: status,
                data: periods.map(p => p.statuses?.find(s => s.status === status)?.count || 0),
                backgroundColor: STATUS_COLORS[status] || '#9F9FF8',
                barThickness: 20,
                borderRadius: 10,
            })),
        };
    })();

    const s2RankingData = (() => {
        if (!dashData) return null;
        const triggers = dashData.intentTriggers || [];
        if (!triggers.length) return null;
        if (isFlat) {
            return {
                labels: triggers.map(i => i.intentName),
                datasets: [{ label: 'Triggers', data: triggers.map(i => i.count), backgroundColor: '#9F9FF8', barThickness: 35, borderRadius: 10 }],
            };
        }
        const periods = triggers.map(p => formatPeriodLabel(p.period));
        const intentNames = [...new Set(triggers.flatMap(p => (p.intents || []).map(i => i.intentName)))];
        if (!intentNames.length) return null;
        return {
            labels: periods,
            datasets: intentNames.map((name, idx) => ({
                label: name,
                data: triggers.map(p => (p.intents || []).find(i => i.intentName === name)?.count || 0),
                backgroundColor: INTENT_COLORS[idx % INTENT_COLORS.length],
                barThickness: 20,
                borderRadius: 10,
            })),
        };
    })();

    const s2TicketVolumeData = (() => {
        if (!dashData) return null;
        if (isFlat) {
            const total = typeof dashData.ticketCounts === 'number' ? dashData.ticketCounts : 0;
            if (!total) return null;
            return {
                labels: [frequency === 'today' ? translateThis('Today') : 'Total'],
                datasets: [{ label: 'Tickets', data: [total], backgroundColor: '#9F9FF8', barThickness: 60, borderRadius: 10 }],
            };
        }
        const periods = dashData.ticketCounts || [];
        if (!periods.length) return null;
        return {
            labels: periods.map(p => formatPeriodLabel(p.period)),
            datasets: [{ label: 'Tickets', data: periods.map(p => p.count), backgroundColor: '#9F9FF8', barThickness: 25, borderRadius: 10 }],
        };
    })();

    const s2TotalConsultas = dashData
        ? (typeof dashData.ticketCounts === 'number'
            ? dashData.ticketCounts
            : (Array.isArray(dashData.ticketCounts) ? dashData.ticketCounts.reduce((sum, p) => sum + p.count, 0) : 0))
        : null;
    const s2HumanReq = dashData
        ? (Array.isArray(dashData.ticketsByStatus)
            ? dashData.ticketsByStatus.reduce((sum, p) => {
                const item = p.statuses ? p.statuses.find(s => s.status === 'Human Req.') : (p.status === 'Human Req.' ? p : null);
                return sum + (item?.count || 0);
            }, 0)
            : 0)
        : null;

    return (

        <PageLayout
            pageName={'Dashboard'}
            pageTitle={'Dashboard'}
            bodyClassName={`module-main-wrp`}
            key={`${appState?.selectedLang}`}
        >

            <Layout className='dashboard-layout'>

                <Layout >

                    <Content>
                        <div className="container">
                            <div className="main-content-wrapper dashboard-module f-wrp">
                                <Row gutter={[24, 24]}>
                                    {/* <Col span={24}>
                                        <Title level={4} style={{ margin: '0' }}>{translateThis('Panel de gestión')}</Title>
                                        <Paragraph style={{ margin: '0' }}>&nbsp;</Paragraph>
                                    </Col> */}
                                    {SHOW_STATIC_CHARTS && <>
                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={11}>
                                            <div className='each-dashboard-items f-wrp'>
                                                <Title level={5} style={{ marginTop: '0' }}>{translateThis('Monitor de gestión en vivo')}</Title>
                                                <br />
                                                {/* <img src={AppIcons.slider1} alt='graph' /> */}

                                                <AppBarChart
                                                    data={{
                                                        labels: [`${translateThis('IH en curso')}`, `${translateThis('Total pendientes')}`, `${translateThis('Resueltos')}`],
                                                        datasets: [
                                                            {
                                                                label: 'RIH',
                                                                data: [3800, 2500, 1500],
                                                                backgroundColor: ['#162550', '#9F9FF8', '#4CE98F'],
                                                                barThickness: 35,
                                                                borderRadius: 10,
                                                            },
                                                            {
                                                                label: 'Pendientes',
                                                                data: [0, 2200, 1700],
                                                                backgroundColor: ['#162550', '#7459D9', '#26AD5F'],
                                                                barThickness: 35,
                                                                borderRadius: 10,
                                                            },
                                                        ],
                                                    }}
                                                    options={{
                                                        scales: {
                                                            x: {
                                                                stacked: true,
                                                                ticks: {
                                                                    color: '#FFFFFF',
                                                                },
                                                                grid: {
                                                                    display: false,
                                                                    borderDash: [5, 5],
                                                                    color: '#ffffff',
                                                                    drawOnChartArea: true,
                                                                },
                                                            },
                                                            y: {
                                                                stacked: true,
                                                                ticks: {
                                                                    color: '#FFFFFF',
                                                                    display: true,
                                                                    callback: (value) => {
                                                                        const labels = {
                                                                            0: '0',
                                                                            1000: '1K',
                                                                            2000: '2K',
                                                                            3000: '3K',
                                                                            4000: '4K',
                                                                            5000: '5K',
                                                                        };
                                                                        return labels[value] || '';
                                                                    },
                                                                },
                                                                grid: {
                                                                    display: false,
                                                                    color: '#ffffff',
                                                                    borderDash: [5, 5],
                                                                    drawOnChartArea: true,
                                                                },
                                                            },
                                                        },
                                                    }}

                                                />

                                            </div>
                                        </Col>
                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={13}>
                                            <div className='each-dashboard-items f-wrp'>
                                                <Title level={5} style={{ marginTop: '0' }}>{translateThis('Foto estática en el tiempo')}</Title>
                                                <Flex horizontal="true" wrap className='dash-inner-box'>
                                                    <div className='each-dash-innerBox'>
                                                        <div className='dash-innerCon-box'>
                                                            <p>{translateThis('Total de consultas')}</p>
                                                            <h2>2400 <span>+1.08%</span></h2>
                                                        </div>
                                                    </div>
                                                    <div className='each-dash-innerBox'>
                                                        <div className='dash-innerCon-box'>
                                                            <p>{translateThis('Interacciones humanas')}</p>
                                                            <h2>400 <span>HOY</span></h2>
                                                        </div>
                                                    </div>
                                                </Flex>
                                            </div>
                                        </Col>
                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={11}>
                                            <div className='each-dashboard-items f-wrp'>
                                                <Title level={5} style={{ marginTop: '0' }}>{translateThis('Ranking de los temas más consultados')}</Title>
                                                <br />
                                                <AppBarChart
                                                    data={{
                                                        labels: [
                                                            ['Tarjetas', 'pérdidas'],
                                                            ['Aumento de', 'límite de', 'crédito'],
                                                            ['Monto no', 'reconocido'],
                                                            ['Extensiones'],
                                                            ['Otros']
                                                        ],
                                                        datasets: [
                                                            {
                                                                label: 'Pendientes',
                                                                data: [0, 0, 0, 0, 0],
                                                                backgroundColor: ['#7459D9', '#7459D9', '#7459D9', '#7459D9', '#7459D9'],
                                                                barThickness: 35,
                                                                borderRadius: 10,
                                                            },
                                                            {
                                                                label: 'RIH',
                                                                data: [20000, 32000, 25000, 40000, 20000],
                                                                backgroundColor: ['#9F9FF8', '#9F9FF8', '#9F9FF8', '#9F9FF8', '#9F9FF8'],
                                                                barThickness: 35,
                                                                borderRadius: 10,
                                                            },

                                                        ],
                                                    }}
                                                    options={{
                                                        scales: {
                                                            x: {
                                                                stacked: true,
                                                                ticks: {
                                                                    color: '#FFFFFF',
                                                                },
                                                                grid: {
                                                                    display: false,
                                                                    borderDash: [5, 5],
                                                                    color: '#ffffff',
                                                                    drawOnChartArea: true,
                                                                },
                                                            },
                                                            y: {
                                                                stacked: true,
                                                                ticks: {
                                                                    color: '#FFFFFF',
                                                                    display: true,
                                                                    callback: (value) => {
                                                                        const labels = {
                                                                            0: '0',
                                                                            10000: '10K',
                                                                            20000: '20K',
                                                                            30000: '30K',
                                                                            40000: '40K',
                                                                            50000: '50K',
                                                                        };
                                                                        return labels[value] || '';
                                                                    },
                                                                },
                                                                grid: {
                                                                    display: false,
                                                                    color: '#ffffff',
                                                                    borderDash: [10, 10],
                                                                    lineWidth: 1,
                                                                    drawOnChartArea: true,
                                                                },
                                                            },
                                                        },
                                                    }}

                                                />
                                            </div>
                                        </Col>
                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={13}>
                                            <div className='each-dashboard-items f-wrp'>
                                                <Title level={5} style={{ marginTop: '0' }}>{translateThis('Dimensionamiento')}</Title>
                                                <br />
                                                <AppBarChart
                                                    data={{
                                                        labels: [
                                                            ['Lunes'],
                                                            ['Martes '],
                                                            ['Miércoles'],
                                                            ['Jueves '],
                                                            ['Viernes'],
                                                            ['Sábado'],
                                                            ['Domingo']
                                                        ],
                                                        datasets: [
                                                            {
                                                                label: 'RIH',
                                                                data: [100, 200, 280, 140, 300, 107, 300],
                                                                backgroundColor: ['#9F9FF8', '#9F9FF8', '#9F9FF8', '#9F9FF8', '#9F9FF8'],
                                                                barThickness: 35,
                                                                borderRadius: 10,
                                                            },
                                                            {
                                                                label: 'Pendientes',
                                                                data: [140, 300, 100, 100, 100, 200, 200],
                                                                backgroundColor: ['#7459D9', '#7459D9', '#7459D9', '#7459D9', '#7459D9'],
                                                                barThickness: 35,
                                                                borderRadius: 10,
                                                            },
                                                        ],
                                                    }}
                                                    options={{
                                                        scales: {
                                                            x: {
                                                                stacked: true,
                                                                ticks: {
                                                                    color: '#FFFFFF',
                                                                },
                                                                grid: {
                                                                    display: false,
                                                                    borderDash: [5, 5],
                                                                    color: '#ffffff',
                                                                    drawOnChartArea: true,
                                                                },
                                                            },
                                                            y: {
                                                                stacked: true,
                                                                ticks: {
                                                                    color: '#FFFFFF',
                                                                    display: true,
                                                                    callback: (value) => {
                                                                        const labels = {
                                                                            0: '00 Qs',
                                                                            100: '100 Qs',
                                                                            200: '200 Qs',
                                                                            300: '300 Qs',
                                                                            400: '400 Qs'
                                                                        };
                                                                        return labels[value] || '';
                                                                    },
                                                                },
                                                                grid: {
                                                                    display: true,
                                                                    color: '#ffffff',
                                                                    borderDash: [10, 10],
                                                                    lineWidth: 1,
                                                                    drawOnChartArea: true,
                                                                },
                                                            },
                                                        },
                                                    }}

                                                />
                                            </div>
                                        </Col>
                                    </>}

                                    <Col span={24}>
                                        <Flex justify="space-between" align="center" wrap gap={8} style={{ margin: '16px 0 8px' }}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Tendencias y análisis')}</Title>
                                            <Flex align="center" gap={12}>
                                                <Select
                                                    allowClear
                                                    placeholder={translateThis('All agents')}
                                                    style={{ minWidth: 160 }}
                                                    value={moduleId}
                                                    onChange={val => { const v = val || null; setModuleId(v); if (v) localStorage.setItem('dashboard_moduleId', v); else localStorage.removeItem('dashboard_moduleId'); }}
                                                    disabled={isLoading}
                                                    options={modules.map(m => ({ label: m.title, value: m.id }))}
                                                />
                                                <Radio.Group
                                                    value={frequency}
                                                    onChange={e => { setFrequency(e.target.value); localStorage.setItem('dashboard_frequency', e.target.value); }}
                                                    buttonStyle="solid"
                                                    size="small"
                                                    disabled={isLoading}
                                                >
                                                    {FREQUENCY_OPTIONS.map(opt => (
                                                        <Radio.Button key={opt.value} value={opt.value}>{translateThis(opt.label)}</Radio.Button>
                                                    ))}
                                                </Radio.Group>
                                            </Flex>
                                        </Flex>
                                    </Col>

                                    <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                        <div className='each-dashboard-items f-wrp'>
                                            <Title level={5} style={{ marginTop: '0' }}>{translateThis('Monitor de gestión en vivo')}</Title>
                                            <br />
                                            <Spin spinning={isLoading}>
                                                {s2MonitorData ? (
                                                    <AppBarChart
                                                        data={s2MonitorData}
                                                        options={{
                                                            scales: { x: xAxisOptions, y: yAxisBase },
                                                            plugins: { legend: { display: !isFlat, labels: { color: '#FFFFFF', boxWidth: 12 } } },
                                                        }}
                                                    />
                                                ) : !isLoading && (
                                                    <p style={{ color: '#fff', textAlign: 'center' }}>{translateThis('No data')}</p>
                                                )}
                                            </Spin>
                                        </div>
                                    </Col>

                                    <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                        <div className='each-dashboard-items f-wrp' style={{ marginBottom: '24px' }}>
                                            <Title level={5} style={{ marginTop: '0' }}>{translateThis('Active Sessions')}</Title>
                                            <div className='active-sessions-card f-wrp' style={{ padding: 0, display: 'flex' }}>
                                                <div className='active-sessions-pulse'>
                                                    <span className='pulse-ring' />
                                                    <span className='pulse-dot' />
                                                </div>
                                                <div className='active-sessions-info'>
                                                    {activeSessions === null ? <Spin size="default" /> : <h1>{activeSessions}</h1>}
                                                    <p>{translateThis('Active Sessions')}</p>
                                                    <span className='live-badge'>● LIVE</span>
                                                </div>
                                            </div>
                                            
                                        </div>

                                        <div className='each-dashboard-items f-wrp'>
                                            <Title level={5} style={{ marginTop: '0' }}>{translateThis('Foto estática en el tiempo')}</Title>
                                            
                                            <Flex horizontal="true" wrap className='dash-inner-box'>
                                                <div className='each-dash-innerBox'>
                                                    <div className='dash-innerCon-box'>
                                                        <p>{translateThis('Total de consultas')}</p>
                                                        {isLoading ? <Spin size="small" /> : <h2>{s2TotalConsultas ?? '-'}</h2>}
                                                    </div>
                                                </div>
                                                <div className='each-dash-innerBox'>
                                                    <div className='dash-innerCon-box'>
                                                        <p>{translateThis('Interacciones humanas')}</p>
                                                        {isLoading ? <Spin size="small" /> : <h2>{s2HumanReq ?? '-'}</h2>}
                                                    </div>
                                                </div>
                                            </Flex>
                                        </div>

                                    </Col>

                                    <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                        <div className='each-dashboard-items f-wrp'>
                                            <Title level={5} style={{ marginTop: '0' }}>{translateThis('Ranking de los temas más consultados')}</Title>
                                            <br />
                                            <Spin spinning={isLoading}>
                                                {s2RankingData ? (
                                                    <AppBarChart
                                                        data={s2RankingData}
                                                        options={{
                                                            scales: { x: xAxisOptions, y: yAxisBase },
                                                            plugins: { legend: { display: !isFlat, labels: { color: '#FFFFFF', boxWidth: 12 } } },
                                                        }}
                                                    />
                                                ) : !isLoading && (
                                                    <p style={{ color: '#fff', textAlign: 'center' }}>{translateThis('No data')}</p>
                                                )}
                                            </Spin>
                                        </div>
                                    </Col>

                                    <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                        <div className='each-dashboard-items f-wrp'>
                                            <Title level={5} style={{ marginTop: '0' }}>{translateThis('Ticket Volume')}</Title>
                                            <br />
                                            <Spin spinning={isLoading}>
                                                {s2TicketVolumeData ? (
                                                    <AppBarChart
                                                        data={s2TicketVolumeData}
                                                        options={{
                                                            scales: {
                                                                x: xAxisOptions,
                                                                y: {
                                                                    ...yAxisBase,
                                                                    grid: { display: true, color: '#ffffff', borderDash: [10, 10], lineWidth: 1, drawOnChartArea: true },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                ) : !isLoading && (
                                                    <p style={{ color: '#fff', textAlign: 'center' }}>{translateThis('No data')}</p>
                                                )}
                                            </Spin>
                                        </div>
                                    </Col>

                                </Row>
                            </div>

                        </div>
                    </Content>
                </Layout>
                <Sider width={275} style={{ 
                    display: 'none'
                     }}>
                    <div className='sider-box f-wrp'>
                        <Title level={5} style={{ marginTop: '0' }}>{translateThis('Insights rápidos')}</Title>

                        <div className='notification-list f-wrp'>
                            <ul>
                                <li className='each-notifications'>
                                    <span><img src={AppIcons.fileIcon} alt='icon' /></span>
                                    <div className='noti-con'>
                                        <h5>El 28% de las consultas de esta semana fueron sobre límites de tarjeta.</h5>
                                        <p>Justo ahora</p>
                                    </div>
                                </li>
                                <li className='each-notifications'>
                                    <span><img src={AppIcons.fileIcon} alt='icon' /></span>
                                    <div className='noti-con'>
                                        <h5>El 28% de las consultas de esta semana fueron sobre límites de tarjeta.</h5>
                                        <p>Justo ahora</p>
                                    </div>
                                </li>
                                <li className='each-notifications'>
                                    <span><img src={AppIcons.fileIcon} alt='icon' /></span>
                                    <div className='noti-con'>
                                        <h5>Las consultas urgentes bajaron respecto a la semana pasada.</h5>
                                        <p>Hace 12 horas atrás</p>
                                    </div>
                                </li>
                                <li className='each-notifications'>
                                    <span><img src={AppIcons.fileIcon} alt='icon' /></span>
                                    <div className='noti-con'>
                                        <h5>El 28% de las consultas de esta semana fueron sobre límites de tarjeta.</h5>
                                        <p>Justo ahora</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <Title level={5} style={{ marginTop: '0' }}>{translateThis('Performance del Bot')}</Title>

                        <div className='notification-list f-wrp'>
                            <ul>
                                <li className='each-notification-box'>
                                    <p><strong>Autogestión:</strong> 76% sin necesidad de agente.</p>
                                </li>
                                <li className='each-notification-box'>
                                    <p><strong>Satisfacción del usuario:</strong> 4.6/5</p>
                                </li>
                                <li className='each-notification-box'>
                                    <p><strong>Autogestión:</strong>  76% sin necesidad de agente.</p>
                                </li>

                            </ul>
                        </div>

                    </div>
                </Sider>
            </Layout>




        </PageLayout >

    )
}
DashboardPage.propTypes = {}

export default DashboardPage;